/**
 * PHARMAKON — pricing engine · the one sale-price resolver of a counter line (cross-module X XWP-77, XD-228).
 *
 * Which price does a counter line carry? One rule, here, for every reader and every writer:
 *
 *   1. a secours line (sold before its lot is in stock) — the counter price the cashier confirmed,
 *      when > 0 (XD-214, source SAISI_SECOURS);
 *   2. the lot's `StockLot.prix_vente_ppa`, when > 0 (the explicit lot, else the FEFO head the sale
 *      consumes first — XD-74; the server chooses the lot, this function prices it) — source LOT;
 *   3. `Produit.ppa`, when > 0 — source PRODUIT;
 *   4. none → unknown (`centimes: null`): the server answers 422 PRIX_VENTE_INCONNU.
 *
 * A price ≤ 0 counts as absent on every branch (XMD-CP-29: a lot received at 0 DA never prices a
 * line at 0 DA). An AMLOR-like product carrying both prices is charged at the sold lot's price;
 * `Produit.ppa` is shown as secondary text when it differs (`ppaProduitSecondaire`). No client-sent
 * price is ever an input. Integer centimes in, integer centimes out (money.js).
 *
 * Canonical source: packages/shared/pricing/ (the server imports its byte-identical copy
 * apps/server/src/utils/pricing/, the SPA `apps/web-pharmacy/src/lib/prix-vente.js`).
 */
import { versCentimes, versTexteDecimal } from "./money.js";

/** Where a resolved sale price comes from (wire values of `source` / `source_prix`). */
export const SOURCE_PRIX_VENTE = Object.freeze({
  LOT: "LOT",
  PRODUIT: "PRODUIT",
  SAISI_SECOURS: "SAISI_SECOURS",
});

/**
 * A price that can be charged: integer centimes > 0, or null (absent, empty, zero, negative, or
 * below the centime).
 * @param {number|string|object|null|undefined} valeur  number, decimal string or Prisma.Decimal
 * @returns {number|null}
 */
export function prixPositifCentimes(valeur) {
  if (valeur === undefined || valeur === null) return null;
  if (typeof valeur === "string" && valeur.trim() === "") return null;
  const centimes = versCentimes(valeur);
  return centimes > 0 ? centimes : null;
}

/**
 * The price a counter line is charged (XD-228).
 *
 * @param {object} [p]
 * @param {{ prix_vente_ppa?: * }|null} [p.lot]          the lot that prices the line (explicit, or the FEFO head)
 * @param {{ ppa?: * }|null} [p.produit]                 the produit
 * @param {{ prix_confirme?: * }|null} [p.secours]       a secours line's declaration (its confirmed price)
 * @returns {{ centimes: number|null, source: string|null }}
 */
export function resoudrePrixVente({ lot = null, produit = null, secours = null } = {}) {
  const saisi = secours ? prixPositifCentimes(secours.prix_confirme) : null;
  if (saisi !== null) return { centimes: saisi, source: SOURCE_PRIX_VENTE.SAISI_SECOURS };
  const deLot = lot ? prixPositifCentimes(lot.prix_vente_ppa) : null;
  if (deLot !== null) return { centimes: deLot, source: SOURCE_PRIX_VENTE.LOT };
  const deProduit = produit ? prixPositifCentimes(produit.ppa) : null;
  if (deProduit !== null) return { centimes: deProduit, source: SOURCE_PRIX_VENTE.PRODUIT };
  return { centimes: null, source: null };
}

/**
 * `Produit.ppa` as secondary text: its centimes when the line is charged at another price (a lot or
 * a confirmed secours price) and `Produit.ppa` > 0 differs from it; null otherwise.
 * @param {{ centimes: number|null, source: string|null }} resolution  from resoudrePrixVente
 * @param {{ ppa?: * }|null} produit
 * @returns {number|null}
 */
export function ppaProduitSecondaire(resolution, produit) {
  if (!resolution || resolution.centimes === null || resolution.source === SOURCE_PRIX_VENTE.PRODUIT) return null;
  const ppa = produit ? prixPositifCentimes(produit.ppa) : null;
  return ppa !== null && ppa !== resolution.centimes ? ppa : null;
}

/**
 * The wire shape of a resolved price (the POS search row's `prix_vente`): decimal strings with two
 * decimals, the source, the lot that prices it (source LOT only) and the secondary `Produit.ppa`.
 *
 * @param {object} p
 * @param {{ id?: string, prix_vente_ppa?: * }|null} [p.lot]
 * @param {{ ppa?: * }|null} [p.produit]
 * @param {{ prix_confirme?: * }|null} [p.secours]
 * @returns {{ montant: string|null, source: string|null, lot_id: string|null, ppa_produit: string|null }}
 */
export function prixVenteAffiche({ lot = null, produit = null, secours = null } = {}) {
  const r = resoudrePrixVente({ lot, produit, secours });
  const secondaire = ppaProduitSecondaire(r, produit);
  return {
    montant: r.centimes === null ? null : versTexteDecimal(r.centimes),
    source: r.source,
    lot_id: r.source === SOURCE_PRIX_VENTE.LOT ? (lot?.id ?? null) : null,
    ppa_produit: secondaire === null ? null : versTexteDecimal(secondaire),
  };
}
